import { AuthService } from '@/auth/services/auth.service';
import { NotificationService } from '@/notificaction/notification.service';
import { GlobalStore } from '@/store/app.store';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { fieldsMatchValidator } from '@edt/auth/lib/validators/fields-match.validator';
import { finalize } from 'rxjs';

@Component({
  selector: 'store-change-password-form',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatProgressSpinner,
  ],
  templateUrl: './change-password-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordFormComponent {
  #fb = inject(FormBuilder);
  #authSrv = inject(AuthService);
  #globalStore = inject(GlobalStore);
  #notificationSrv = inject(NotificationService);
  frm: FormGroup;

  protected visibleOldPassword = false;
  protected visiblePassword = false;

  protected loading = signal(false);

  submitBtn = viewChild.required<MatButton>('submitButton');
  inputs = viewChildren<MatInput>(MatInput);

  constructor() {
    this.frm = this.#fb.group(
      {
        old_password: ['', [Validators.required, Validators.minLength(4)]],
        password: ['', [Validators.required, Validators.minLength(4)]],
        confirm_password: ['', [Validators.required]],
      },
      {
        validators: fieldsMatchValidator('password', 'confirm_password'),
      }
    );

    effect(() => {
      if (this.loading()) {
        this.old_password.disable();
        this.password.disable();
        this.confirm_password.disable();
      } else {
        this.old_password.enable();
        this.password.enable();
        this.confirm_password.enable();
      }
    });
  }

  onSubmit() {
    const email = this.#globalStore.authUserData()!.email;
    const newPassword = this.password.getRawValue();
    const oldPassword = this.old_password.getRawValue();
    this.inputs().forEach(elem => {
      elem.disabled = true;
    });
    this.loading.set(true);
    this.#authSrv
      .changePasswordByCustomer(email, newPassword, oldPassword)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: () => {
          this.frm.reset();
          this.frm.markAsPristine();
          this.#notificationSrv.showError(
            'Tu contraseña ha sido cambiada con éxito. Por seguridad, cierra todas las sesiones activas excepto esta.'
          );
        },
        error: () =>
          this.#notificationSrv.showError(
            'No se pudo completar la acción. Revisa tu conexión e inténtalo nuevamente.'
          ),
      });
  }

  get old_password() {
    return this.frm.controls['old_password'];
  }
  get password() {
    return this.frm.controls['password'];
  }
  get confirm_password() {
    return this.frm.controls['confirm_password'];
  }
}
